// useVisitBrief — loads one visit's pre-visit brief, asks the server for one
// when the visit has none, and polls every POLL_MS while it is being generated.
// The scheduled sweep writes briefs about an hour before the appointment; a
// chart opened at any other time (days early, or after the appointment) gets
// one on open instead (and the sweep refreshes a brief made more than 12 hours
// before the visit).
import { useCallback, useEffect, useRef, useState } from "react";
import { fetchVisitBrief, requestVisitBrief } from "../../../services/visitBriefService";

export const POLL_MS = 3000;
export const POLL_LIMIT_MS = 3 * 60 * 1000;

// Shown (as requestError) when polling gives up while the server still says
// "generating", so the strip can offer Retry instead of spinning forever.
const SLOW_MESSAGE = "The brief is taking longer than usual.";

export default function useVisitBrief({ schema, visitId, enabled = true }) {
  const key = enabled && visitId ? `${schema || "public"}::${visitId}` : "";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(Boolean(key));
  const [pending, setPending] = useState(false);
  const [requestError, setRequestError] = useState(null);
  const keyRef = useRef(key);
  const timerRef = useRef(null);
  const pollStartRef = useRef(0);
  const autoRequestedRef = useRef("");

  const stopPolling = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const load = useCallback(async (forKey) => {
    try {
      const res = await fetchVisitBrief({ schema, visitId });
      if (keyRef.current !== forKey) return null;
      setData(res);
      setRequestError(null);
      return res;
    } catch (err) {
      if (keyRef.current !== forKey) return null;
      setRequestError(err?.message || "Could not load the brief.");
      return null;
    }
  }, [schema, visitId]);

  const poll = useCallback((forKey) => {
    stopPolling();
    timerRef.current = setTimeout(async () => {
      timerRef.current = null;
      if (keyRef.current !== forKey) return;
      const res = await load(forKey);
      if (res?.status !== "generating" || keyRef.current !== forKey) return;
      if (Date.now() - pollStartRef.current < POLL_LIMIT_MS) {
        poll(forKey);
      } else {
        // Out of patience with the data untouched (any old brief stays visible).
        setRequestError(SLOW_MESSAGE);
      }
    }, POLL_MS);
  }, [load, stopPolling]);

  const request = useCallback(async (trigger) => {
    const forKey = keyRef.current;
    if (!forKey) return;
    setRequestError(null);
    setPending(true);
    try {
      await requestVisitBrief({ schema, visitId, trigger });
      if (keyRef.current !== forKey) return;
      setData((prev) => ({ ...(prev || {}), status: "generating" }));
      pollStartRef.current = Date.now();
      poll(forKey);
    } catch (err) {
      if (keyRef.current === forKey) setRequestError(err?.message || "Could not start the brief.");
    } finally {
      if (keyRef.current === forKey) setPending(false);
    }
  }, [schema, visitId, poll]);

  useEffect(() => {
    keyRef.current = key;
    stopPolling();
    setData(null);
    setPending(false);
    setRequestError(null);
    if (!key) {
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    (async () => {
      const res = await load(key);
      if (cancelled) return;
      setLoading(false);
      if (res?.status === "generating") {
        pollStartRef.current = Date.now();
        poll(key);
      } else if (res?.status === "none" && autoRequestedRef.current !== key) {
        autoRequestedRef.current = key;
        request("open");
      }
    })();
    return () => {
      cancelled = true;
      keyRef.current = "";
      stopPolling();
    };
    // Keyed on the visit alone: load/poll/request are rebuilt from the same
    // schema + visitId that `key` encodes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return {
    loading,
    data,
    pending,
    requestError,
    regenerate: () => request("manual"),
  };
}
